/* eslint-disable custom/no-restricted-translation-jsx-untranslated-children-text */
import React from 'react';

import { Typography } from '@mui/material';

import { useIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';
import { NavLink } from '@kamernet/core/Routing/NavLink';
import { ROUTES } from '@kamernet/core/Routing/routes';
import { ListingsSearchCategory } from '@kamernet/core/Routing/routes/ListingsSearch';

import styles from './Content.module.css';

export const ContentCountryFurnishedApartments = () => {
  const { locale } = useIntl();
  const searchCategories = [ListingsSearchCategory.FurnishedApartment];

  return locale === Locale.en ? (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        Furnished Apartments for Rent in Netherlands
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Furnished apartments in Netherlands are ideal for those seeking flexible
        and comfortable housing without the need to furnish a home themselves.
        Kamernet provides access to a broad selection of furnished rentals in
        Netherlands that meet different preferences and budgets. Whether you’re
        relocating for work or staying temporarily, furnished apartments in
        Netherlands offer a convenient alternative to hotels or extended
        commutes. Many furnished apartments for rent in Netherlands are move-in
        ready and come with essential items, allowing tenants to settle in
        easily and manage their stay with minimal effort.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Short-Term vs. Long-Term Furnished Apartments in Netherlands
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Furnished apartments in Netherlands are suitable for both short-term and
        long-term stays. While typically more expensive than unfurnished
        options, they provide added convenience, especially for those who prefer
        not to purchase or move furniture. Short-term renters often choose
        furnished housing for flexibility and ease of relocation, while
        long-term tenants may find it practical for temporary assignments or
        transitional living. In both cases, the higher rent is often balanced by
        the time and effort saved. It’s important to assess your length of stay,
        budget, and lifestyle before deciding between furnished and unfurnished
        rentals in Netherlands.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        What’s Included in a Furnished Apartment in Netherlands?
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Furnished apartments in Netherlands typically include essential items
        such as a bed, sofa, dining table, and chairs. A fully furnished or
        turnkey apartment goes further, often providing cookware, utensils, bed
        linens, towels, and small appliances like a coffee maker. It’s always a
        good idea to confirm with the landlord what’s included and whether
        you’ll need to bring any personal items, such as bedding or toiletries.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Why Choose Kamernet for Furnished Apartments
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Kamernet makes it easy to find furnished apartments across Netherlands,
        with new listings added regularly in major cities and local
        neighborhoods. Our platform gives you quick access to exclusive options
        not listed on other sites, with no middlemen or agency fees involved.
        From short stays to longer leases, you’ll find a variety of
        move-in-ready homes that best match your needs. If you’re searching for
        furnished apartments in Netherlands, Kamernet offers a straightforward
        way to rent with transparency and confidence.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Popular Cities:
      </Typography>
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'amsterdam', searchCategories },
          Locale.en,
        )}
      >
        Amsterdam Furnished Apartments
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'rotterdam', searchCategories },
          Locale.en,
        )}
      >
        Rotterdam Furnished Apartments
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'den-haag', searchCategories },
          Locale.en,
        )}
      >
        Den Haag Furnished Apartments
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'eindhoven', searchCategories },
          Locale.en,
        )}
      >
        Eindhoven Furnished Apartments
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'groningen', searchCategories },
          Locale.en,
        )}
      >
        Groningen Furnished Apartments
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'utrecht', searchCategories },
          Locale.en,
        )}
      >
        Utrecht Furnished Apartments
      </NavLink.Anchor>
      <Typography variant="h3" className={styles.title}>
        Popular Neighbourhoods:
      </Typography>
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'amstelveen', street: 'uilenstede', searchCategories },
          Locale.en,
        )}
      >
        Uilenstede Furnished Apartments
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'amsterdam', street: 'bijlmerdreef', searchCategories },
          Locale.en,
        )}
      >
        Bijlmerdreef Furnished Apartments
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'rotterdam', street: 'zuidplein', searchCategories },
          Locale.en,
        )}
      >
        Zuidplein Furnished Apartments
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'leiden', street: 'klikspaanweg', searchCategories },
          Locale.en,
        )}
      >
        Klikspaanweg Furnished Apartments
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'diemen', street: 'rode-kruislaan', searchCategories },
          Locale.en,
        )}
      >
        Rode Kruislaan Furnished Apartments
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'den-haag', street: 'leyweg', searchCategories },
          Locale.en,
        )}
      >
        Leyweg Furnished Apartments
      </NavLink.Anchor>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        Gemeubileerde appartementen te huur in Nederland
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Een gemeubileerd appartement huren in Nederland is ideaal voor mensen
        die op zoek zijn naar flexibele en comfortabele woonruimte, zonder zelf
        meubels te hoeven regelen. Kamernet biedt toegang tot een breed aanbod
        van gemeubileerde woningen in Nederland die passen bij verschillende
        voorkeuren en budgetten. Of je nu voor werk verhuist of tijdelijk
        verblijft, gemeubileerd huren in Nederland is een handig alternatief
        voor hotels of lange reistijden. Veel van deze appartementen zijn
        instapklaar en uitgerust met essentiële voorzieningen. Zo kun je
        makkelijk intrekken en zonder gedoe aan je verblijf beginnen.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Kortetermijn vs. Langetermijn Gemeubileerde Woningen in Nederland
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Een gemeubileerd appartement in Nederland is geschikt voor zowel korte
        als langere verblijven. Dit type woning is meestal iets duurder dan een
        ongemeubileerd appartement, maar biedt meer gemak. Dat is vooral prettig
        voor wie geen meubels wil kopen of verhuizen. Kortetermijnhuurders
        kiezen vaak voor gemeubileerde kamers of appartementen vanwege de
        flexibiliteit. Voor langetermijnhuurders is het een praktische oplossing
        bij tijdelijke werkopdrachten of in een overgangsfase. De hogere
        huurprijs wordt vaak gecompenseerd door de tijd en moeite die je
        bespaart. Denk daarom goed na over je verblijfsduur, budget en
        levensstijl voordat je een gemeubileerde woning huurt in Nederland.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Wat is Inbegrepen bij Gemeubileerd Huren in Nederland?
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Een gemeubileerde woning in Nederland bevat meestal basis meubels zoals
        een bed, bank, eettafel en stoelen. Een volledig gemeubileerd of
        ‘turnkey’ appartement biedt vaak ook kookgerei, bestek, beddengoed,
        handdoeken en kleine apparaten zoals een koffiezetapparaat. Vraag altijd
        even aan de verhuurder wat er precies inbegrepen is, en of je zelf nog
        persoonlijke spullen moet meenemen, zoals beddengoed of toiletartikelen.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Waarom Gemeubileerd Huren in Nederland via Kamernet?
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Kamernet maakt het eenvoudig om een gemeubileerd appartement te huren in
        heel Nederland. Er worden regelmatig nieuwe woningen aangeboden in zowel
        grote steden als lokale buurten. Ons platform geeft je snel toegang tot
        exclusieve gemeubileerde woningen die je niet op andere websites vindt.
        Je huurt direct van de aanbieder, zonder tussenpersonen of
        makelaarskosten. Of je nu op zoek bent naar een gemeubileerde kamer in
        Nederland voor een kort verblijf of naar een appartement voor een
        langere periode, bij Kamernet vind je altijd instapklare woningen die
        aansluiten bij jouw wensen. Als je gemeubileerd wilt huren in Nederland,
        biedt Kamernet een eenvoudige en betrouwbare oplossing.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Populaire steden:
      </Typography>
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'amsterdam', searchCategories },
          Locale.nl,
        )}
      >
        Gemeubileerd huren Amsterdam
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'rotterdam', searchCategories },
          Locale.nl,
        )}
      >
        Gemeubileerd huren Rotterdam
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'den-haag', searchCategories },
          Locale.nl,
        )}
      >
        Gemeubileerd huren Den Haag
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'eindhoven', searchCategories },
          Locale.nl,
        )}
      >
        Gemeubileerd huren Eindhoven
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'groningen', searchCategories },
          Locale.nl,
        )}
      >
        Gemeubileerd huren Groningen
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'utrecht', searchCategories },
          Locale.nl,
        )}
      >
        Gemeubileerd huren Utrecht
      </NavLink.Anchor>
      <Typography variant="h3" className={styles.title}>
        Populaire buurten:
      </Typography>
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'amstelveen', street: 'uilenstede', searchCategories },
          Locale.nl,
        )}
      >
        Gemeubileerd huren Uilenstede
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'amsterdam', street: 'bijlmerdreef', searchCategories },
          Locale.nl,
        )}
      >
        Gemeubileerd huren Bijlmerdreef
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'rotterdam', street: 'zuidplein', searchCategories },
          Locale.nl,
        )}
      >
        Gemeubileerd huren Zuidplein
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'leiden', street: 'klikspaanweg', searchCategories },
          Locale.nl,
        )}
      >
        Gemeubileerd huren Klikspaanweg
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'diemen', street: 'rode-kruislaan', searchCategories },
          Locale.nl,
        )}
      >
        Gemeubileerd huren Rode Kruislaan
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'den-haag', street: 'leyweg', searchCategories },
          Locale.nl,
        )}
      >
        Gemeubileerd huren Leyweg
      </NavLink.Anchor>
    </React.Fragment>
  );
};
